// A sheet that IS a list of things in the project — and the diff back.
//
// The Usages sheet was the first of these, and Features and Blocks are the same
// shape: one row per thing, one column per field, ordinary values you type over,
// and a diff computed when asked rather than a staging area to keep in sync.
// Three copies of that would be three copies of the two bugs it took to get
// right — rows AND columns matched by NAME, never by position, and a remembered
// baseline so a change made in Giraffe is not mistaken for one the user typed.
//
// So it lives once, here, parameterised by what a row IS.

import type { Cell, CellValue, ColumnList, Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { getCell, setCellValue } from '@/model/workbook';
import { applyStructural, deleteBand, deletionMap, insertBand } from '@/model/structure';
import { prettyField } from '@/data/classify';
import { formatForField } from '@/engine/format';
import {
  columnHeader,
  dimensionOf,
  headerAliases,
  reunit,
  reunitFields,
  toProjectValue,
  unitSuffix,
  type Dimension,
  type UnitSystem,
} from '@/data/units';

export const HEADER_ROW = 1;
export const FIRST_DATA_ROW = 2;

/**
 * Where a field's value on one row came from.
 *
 * `raw` is a genuine property the project (or the user) set — editable,
 * pushed on change, like every field before this existed. `inherited` and
 * `calculated` are both read-only shown-not-typed values with no raw
 * property backing them yet; the difference is only what an edit DOES:
 * editing an `inherited` cell is how one is given a raw property of its own
 * (a real-world input the user is overriding), while `calculated` is pure
 * engine/geometry output with no "override" to make — it is never
 * promotable, whatever is typed into it is discarded on the next rewrite.
 *
 * Absent entirely (`EntityRow.provenance` undefined) means every field on
 * that row is `raw` — Usages' and Blocks' rows never set this, so nothing
 * about their read-only/push behaviour changes.
 */
export type CellProvenance = 'raw' | 'inherited' | 'calculated';

/** One thing in the project, as a table row. */
export interface EntityRow {
  /** The label in column one. The match key too, UNLESS `key` is set below. */
  name: string;
  /**
   * The stable identifier this row is actually matched/diffed by, when two
   * rows can legitimately show the identical `name` (Features — a
   * feature's own raw id; Areas — same, used by "Select on map", not by
   * `writeTable`/`tableDiff`, which never touch Areas' read-only sheet).
   * Absent for Usages and Blocks, where every name really is unique and
   * `name` itself is still the match key, unchanged.
   */
  key?: string;
  /** What the project says each field is, right now. */
  fields: Record<string, unknown>;
  /**
   * Per-field origin, keyed the same as `fields`. A field missing from this
   * map (or the map itself being absent) is `raw` — the ordinary case for
   * every table but Features, where a value can come from a baked section or
   * a matching Usage instead of the feature's own properties.
   */
  provenance?: Record<string, CellProvenance>;
}

/** The match key a row is actually found/diffed by — its `key` if set, else its `name`. */
function matchKeyOf(row: EntityRow): string {
  return row.key ?? row.name;
}

export interface TableSpec {
  /** The header over column one — "Name", "Feature", "Block". */
  label: string;
  /** Fields that ARE the label, so they are not repeated as columns. */
  labelFields: ReadonlySet<string>;
  /** Preferred order for the familiar fields. Everything else follows it. */
  order: readonly string[];
  /** Fields shown but never pushed — computed, or the project's own bookkeeping. */
  readOnly?: ReadonlySet<string>;
  /**
   * Fields grouped together after every other field, instead of interleaving
   * with `order`'s editable fields around them — the project's baked/computed
   * properties, read by eye rather than typed into.
   *
   * A subset of `readOnly` in practice, but kept separate from it: not every
   * `readOnly` field should move — Features' own Show switch is never pushed
   * either, but it is a control the user reaches for, not a fact to read off
   * the right edge, and it has a column width hard-coded to where it sits.
   *
   * Undefined/empty for Usages and Blocks, so their layout is unchanged.
   */
  rightAlign?: ReadonlySet<string>;
  /** Fields that are structures rather than numbers, and sort to the end. */
  isNested?: (field: string) => boolean;
  /**
   * Fields that get no column at all — keyed by `squashField`.
   *
   * Different from `readOnly`, which shows a field and declines to push it.
   * This is for fields that are not information about the thing: the handles
   * the host addresses it by. A column of opaque ids is width the numbers
   * beside it could have used, and there is nothing to read in it.
   */
  hidden?: ReadonlySet<string>;
  /**
   * Fixed lists to hang on a field's column, so it is picked rather than typed.
   *
   * Applied on every write and keyed by FIELD, not by column index, because the
   * user may have moved the column and the list belongs to the field.
   */
  lists?: Readonly<Record<string, ColumnList>>;
  /**
   * The units the ROWS are in — whatever the provider says it handed out.
   *
   * The table only labels its headers with it. The values arrive converted
   * already (data/units.ts wraps the provider), and undoing that on the way
   * back to the project is `toProjectUnits` below.
   *
   * `null` for a table whose columns are not project measures at all: the
   * Blocks sheet, where the headers are typed by the user. Labelling one of
   * those "Area (m²)" would be a claim nobody made — and rewriting the header
   * someone typed is not this app's business.
   */
  units: UnitSystem | null;
  /**
   * Whether a header the table does not recognise is a NEW field.
   *
   * Typing "Time" over a fresh column and filling it down is the obvious way to
   * give every usage a time, and without this the column was invisible to the
   * diff — the values sat there and nothing was ever pushed.
   */
  newFields?: boolean;
  /**
   * Force every row into a freshly sorted physical position on every write
   * (user-requested — Features should match Areas' guaranteed layer/
   * building/usage/name order instead of leaving a row wherever it already
   * sits). An unpushed edit still survives: its CELL is carried forward to
   * the row's new position rather than left behind at the old one — see
   * `writeTable`'s own doc comment on `carried`.
   *
   * Unset for Usages and Blocks: a manual row reorder there is still
   * respected, the original "keep a row wherever it already sits" default.
   */
  forceSort?: boolean;
  /**
   * Override `columnsOf`'s generic rank sort with a table-specific one.
   *
   * Takes every discovered field (after `labelFields`/`hidden` are already
   * removed) and returns them in the order the table wants. Only Features
   * sets this — its four-tier "raw fields, then inherited, then calculated,
   * then nothing (custom-calc columns are formulas appended separately, not
   * fields)" order depends on per-ROW provenance across the whole sheet,
   * which a static per-field rank cannot express. Usages and Blocks omit it
   * and get the rank sort below, unchanged.
   */
  columnSort?: (fields: string[]) => string[];
  /**
   * Header text is the field's literal JSON name, not `prettyField`'s
   * humanised spelling and not a unit suffix — so "netArea" reads "netArea",
   * not "Net Area (m²)". Only Features sets this: its columns are a live
   * project's own property names, and spelling them exactly as the project
   * does is what lets someone match a cell here back to the same name in
   * Giraffe's own Edit Properties panel or an exported feature. Reading a
   * sheet back in still accepts either spelling (`headerAliases` already
   * lists the raw field name as one), so an older save with pretty headers
   * is not orphaned.
   *
   * A predicate rather than a flat switch so a synthetic, non-JSON field —
   * Features' own Show switch — can opt back out and keep its pretty label.
   */
  rawHeaders?: boolean | ((field: string) => boolean);
}

/**
 * Every field the rows carry, ordered: the familiar ones first in a familiar
 * order, then the rest alphabetically, with nested values last — they are
 * context beside the numbers rather than the point of the table.
 *
 * Discovered, never a whitelist. A whitelist is how the usages table lost a real
 * project's colour, floor-to-floor and everything else it did not name.
 */
/**
 * A field name or a header down to letters and digits: `appId`, `appID`,
 * `app_id` and the header "App Id" all become `appid`.
 *
 * How `TableSpec.hidden` is matched, in both directions. A field's header is
 * `prettyField`'s spelling of it, and `appId` and `appID` pretty-print
 * differently — which spelling a given Giraffe build emits should not decide
 * whether a column comes back.
 */
/** Memoised: `findField` squashes every key of every row, and the set of keys is small. */
const squashed = new Map<string, string>();

export function squashField(name: string): string {
  let hit = squashed.get(name);
  if (hit === undefined) {
    hit = name.replace(/[^A-Za-z0-9]+/g, '').toLowerCase();
    if (squashed.size > 5000) squashed.clear();
    squashed.set(name, hit);
  }
  return hit;
}

/**
 * A property read that matches by `squashField`, not by exact spelling.
 * Confirmed live: Giraffe does not emit every field under one fixed name
 * across projects — a raw feature's building id arrived as `buildingID`
 * (no underscore, capital ID), not the `_buildingId` a literal
 * `properties._buildingId` access assumes — so a feature that has the data
 * under a different spelling reads as simply not having it. Use this
 * instead of a literal property access for any field whose exact spelling
 * is not a hardcoded internal name gTables itself writes.
 */
export function findField(properties: Record<string, unknown> | undefined, name: string): unknown {
  if (!properties) return undefined;
  let index = fieldIndex.get(properties);
  if (index === undefined) {
    index = new Map();
    for (const [k, v] of Object.entries(properties)) {
      const s = squashField(k);
      if (!index.has(s)) index.set(s, v); // first spelling wins, as the old linear scan did
    }
    fieldIndex.set(properties, index);
  }
  return index.get(squashField(name));
}

/**
 * Squashed-key index per properties object. `mergeAreaRows` asks each section
 * for `type`, `levels`, `buildingID`, `stackOrder`, … — several times per level
 * and inside a sort comparator — and each ask used to walk every property.
 * Giraffe's pushed rows are never mutated in place (a push is a new object),
 * so an index keyed by identity stays correct and dies with the row.
 */
const fieldIndex = new WeakMap<object, Map<string, unknown>>();

export function columnsOf(rows: readonly EntityRow[], spec: TableSpec): string[] {
  const fields = new Set<string>();
  for (const row of rows) for (const f of Object.keys(row.fields)) fields.add(f);
  for (const f of spec.labelFields) fields.delete(f);
  if (spec.hidden !== undefined) {
    for (const f of [...fields]) if (spec.hidden.has(squashField(f))) fields.delete(f);
  }
  if (spec.columnSort) return spec.columnSort([...fields]);
  const rank = (f: string): number => {
    if (spec.isNested?.(f)) return 5;
    const known = spec.order.indexOf(f) >= 0;
    const grouped = spec.rightAlign?.has(f) === true;
    if (grouped) return known ? 3 : 4;
    return known ? 1 : 2;
  };
  return [...fields].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 1 || ra === 3) return spec.order.indexOf(a) - spec.order.indexOf(b);
    return naturalCompare(a, b);
  });
}

/**
 * The default ROW order, shared by Features and Areas: by `layerId`, in the
 * project's own layer-tree order (best-effort, not alphabetical — see
 * `giraffe/normalize.ts`'s `drawingLayerNames`, which no longer sorts its
 * result) · then `buildingID` · then `levelNumber` — which floor of the
 * whole BUILDING, not just this section's own stack, so a podium's floors
 * interleave with the tower's correctly instead of every section's "floor
 * 1" sorting together — falling back to `stackSection` where there is no
 * `levelNumber` to rank by (no `buildingID` at all: every Features row, and
 * an Areas row whose own building was never named) · then `usage` · then
 * the row's own display name, as the final tie-break.
 *
 * A `layerId` the project's layer tree does not recognise (never drawn on
 * yet, or typed off-list) sorts AFTER every known layer, not before or
 * mixed in among them — same tolerant-but-last treatment `layerOptions`
 * already gives an off-list value elsewhere. `buildingID`/`usage` absent
 * entirely fall back to an empty string, equal to every other row with
 * nothing there either, so the next key decides.
 */
/**
 * Every name in `names`, in the project's own layer-tree order — the same
 * order `sortRows` ranks rows by, so a dropdown built from this reads the
 * same top-to-bottom as the sheet itself does. A name the tree does not
 * recognise (never drawn on yet, or typed off-list) sorts AFTER every known
 * layer, alphabetically among any others like it — tolerant, not silently
 * dropped, same as `sortRows`'s own treatment of an off-list `layerId`.
 */
export function orderByLayerTree(names: Iterable<string>, layerOrder: readonly string[]): string[] {
  const rank = new Map(layerOrder.map((l, i) => [l, i]));
  const rankOf = (name: string): number => rank.get(name) ?? layerOrder.length;
  return [...names].sort((a, b) => {
    const byRank = rankOf(a) - rankOf(b);
    if (byRank !== 0) return byRank;
    return naturalCompare(a, b);
  });
}

export function sortRows(rows: EntityRow[], layerOrder: readonly string[]): void {
  const layerRank = new Map(layerOrder.map((l, i) => [l, i]));
  const rankOf = (v: unknown): number => (typeof v === 'string' && layerRank.has(v) ? layerRank.get(v)! : layerOrder.length);
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
  rows.sort((a, b) => {
    const byLayer = rankOf(a.fields.layerId) - rankOf(b.fields.layerId);
    if (byLayer !== 0) return byLayer;
    const byBuilding = naturalCompare(str(a.fields.buildingID), str(b.fields.buildingID));
    if (byBuilding !== 0) return byBuilding;
    const byLevel = num(a.fields.levelNumber ?? a.fields.stackSection) - num(b.fields.levelNumber ?? b.fields.stackSection);
    if (byLevel !== 0) return byLevel;
    const byUsage = naturalCompare(str(a.fields.usage), str(b.fields.usage));
    if (byUsage !== 0) return byUsage;
    return naturalCompare(a.name, b.name);
  });
}

/**
 * Put a restored system sheet's column order back, before it is filled.
 *
 * The cells of a system sheet are not saved — `writeTable` rebuilds them from
 * the project — but the column ORDER lived in those header cells, and `filters`
 * and `colWidths` are keyed by column index, so an order that resets repoints
 * every one of them at a different field. Writing the saved header text back is
 * enough, because the matching below is by header anyway.
 *
 * Returns true when this WAS a restore — the sheet arrived with no cells — so
 * the caller knows to re-apply the saved sort too. On every later broadcast the
 * sheet already holds its columns and rows, and `writeTable` keeps them.
 */
export function restoreArrangement(sheet: Sheet): boolean {
  if (Object.keys(sheet.cells).length > 0) return false;
  const headers = sheet.headers ?? [];
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i];
    if (h !== null && h !== undefined && h !== '') setCellValue(sheet, HEADER_ROW, i + 1, h);
  }
  return true;
}

export function bold(sheet: Sheet, row: number, col: number): void {
  const key = `${row},${col}`;
  sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, bold: true } };
}

/**
 * The colour a cell is shown in, by provenance — `raw` (a value on the
 * feature itself, including every Areas cell now that it is not swept to
 * `calculated` wholesale) is the sheet's ordinary text colour, `inherited`
 * (an Areas value copied down from its matching Usage row, nobody typed)
 * is `var(--gt-muted)`, and `calculated` (pure engine/geometry output, or a
 * gTables-synthesised field like `stackSection`) is `var(--gt-accent)` — the
 * same blue the rest of the app already uses for anything "the system
 * decided this, not you". Both are CSS variables, not literal hex, so they
 * track the sheet's own light/dark theme rather than fighting it.
 *
 * Set unconditionally on every data cell (never left to a stale colour from
 * a row that used to be a different provenance): `colourCell`/`clearCellColor`
 * and `writeTable` calls exactly one on every cell every rewrite, so a
 * promoted cell's colour changes the moment its provenance does and nothing
 * lingers from the write before.
 */
export function colourCell(sheet: Sheet, row: number, col: number, colour: string): void {
  const key = `${row},${col}`;
  sheet.cells[key] = { ...sheet.cells[key], s: { ...sheet.cells[key]?.s, color: colour } };
}

function clearCellColor(sheet: Sheet, row: number, col: number): void {
  const key = `${row},${col}`;
  const cell = sheet.cells[key];
  if (cell?.s?.color === undefined) return;
  const { color: _color, ...rest } = cell.s;
  sheet.cells[key] = { ...cell, s: Object.keys(rest).length > 0 ? rest : undefined };
}

/** The key a cell's last-known project value is remembered under. */
function baseKey(name: string, field: string): string {
  return `${name}\u0000${field}`;
}

/** ...and the field back out of one, for a pass over the whole baseline. */
function fieldOfBaseKey(key: string): string {
  return key.slice(key.indexOf('\u0000') + 1);
}

/** How far right to look for headers. Generous: the user can add columns. */
function scanWidth(sheet: Sheet, columns: string[]): number {
  return Math.max(sheet.cols, columns.length + 8);
}

/**
 * Which column holds the row labels.
 *
 * Wherever the header "Name" is, not column 1 by decree — a user who moved or
 * inserted a column has not broken anything, and reading labels out of the
 * wrong column would report every usage as deleted.
 */
export function nameColumn(sheet: Sheet, columns: string[], spec: TableSpec): number | null {
  for (let c = 1; c <= scanWidth(sheet, columns); c++) {
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v !== undefined && v !== null && String(v).trim().toLowerCase() === spec.label.toLowerCase()) return c;
  }
  return null;
}

/**
 * Clears every data row's NAME-column cell, so the `writeTable` call right
 * after this one finds no existing row for ANY name — its own row-matching
 * (`rowsByName`) deliberately keeps a row wherever it already sits (see
 * `writeTable`'s own comment: "a usage keeps the row it is already on, so
 * a sheet the user has sorted or reordered is not shuffled back underneath
 * them"), which is exactly right for not fighting a manual reorder but
 * means a CALLER-COMPUTED sort order (`sortRows`, above) never takes
 * physical effect on a row that already has one from an earlier write —
 * confirmed live: a sheet written under an old row order kept that exact
 * physical layout forever, no matter how `sortRows` itself changed.
 *
 * With nothing left to match, every row in the array that follows lands on
 * a FRESH slot in strict array order (`writeTable`'s own `rowFor`
 * fallback), which is exactly `sortRows`'s intended order.
 *
 * Only the label cell — every other field cell is about to be overwritten
 * anyway by the write that follows, for a sheet (Areas) with nothing to
 * preserve across writes in the first place; clearing more than this one
 * column would just be work `writeTable` is about to redo. NOT used for
 * Features: an unpushed edit there is matched to its row by name through
 * the very same `rowsByName` lookup this clears, so doing this on a sheet
 * that still needs that match would discard a live edit instead of merely
 * reordering around it.
 *
 * Also clears `entityKeys` (Areas rows now carry `EntityRow.key` too, for
 * "Select on map" — see its own doc comment): `writeTable`'s key-based
 * resolution would otherwise still find a row by its OLD key-to-row-number
 * mapping even with the name cell blanked, silently undoing the whole
 * point of this function.
 */
export function resetRowOrder(sheet: Sheet, rows: readonly EntityRow[], spec: TableSpec): void {
  const columns = columnsOf(rows, spec);
  const nameCol = nameColumn(sheet, columns, spec) ?? 1;
  for (let r = FIRST_DATA_ROW; r <= sheet.rows + 40; r++) delete sheet.cells[`${r},${nameCol}`];
  sheet.entityKeys = undefined;
}

/**
 * Which column each field lives in, read off the header row the user sees.
 *
 * BY NAME, never by position — the same rule the rows already follow. Read
 * positionally, deleting one column shifts every field one to the left, and the
 * push then writes each value into its neighbour's field: a real project came
 * back with `"strokeWidth": "genericPoint"` and `"type": ""` straight through
 * the usage list. A field with no column is simply absent, and is left alone.
 */
export function fieldColumns(sheet: Sheet, columns: string[]): Map<string, number> {
  const byHeader = new Map<string, string>();
  /*
   * Two passes, not one: a field's own EXACT name always wins a collision.
   * `headerAliases` also registers a "pretty" alias with the leading
   * underscore stripped (`units.ts`'s `prettyField`), so `height` and
   * `_height` — two different, real fields on a sheet that shows raw
   * headers — both claim the alias "height". A single pass set the map in
   * field iteration order, so whichever field came last silently stole the
   * other's existing column, and the orphaned field got a brand-new column
   * inserted on every single write. Confirmed live: the same "height"
   * column, inserted roughly sixty times over one long session. An exact
   * field-name alias is unique by construction — every field in `columns`
   * is a distinct string — and must never be shadowed by another field's
   * looser alias.
   */
  for (const f of columns) byHeader.set(f.toLowerCase(), f);
  for (const f of columns) {
    // Every OTHER spelling, unit-bearing ones included: a column headed
    // "Gross Area" and one headed "Gross Area (ft²)" are the same column,
    // and a saved sheet holds whichever was written when it was last open.
    for (const alias of headerAliases(f)) {
      if (!byHeader.has(alias)) byHeader.set(alias, f);
    }
  }
  const out = new Map<string, number>();
  for (let c = 1; c <= scanWidth(sheet, columns); c++) {
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v === undefined || v === null) continue;
    const field = byHeader.get(String(v).trim().toLowerCase());
    if (field !== undefined && !out.has(field)) out.set(field, c);
  }
  return out;
}

/**
 * Self-heals the specific corruption `fieldColumns`' two-pass fix (above)
 * now prevents from happening AGAIN, but cannot undo on its own — a column
 * already duplicated by the old alias-collision bug stays duplicated
 * forever otherwise, since nothing else ever claims or clears it. Confirmed
 * live: one real sheet had accumulated SIXTY columns all headed `height`,
 * one more inserted on every write for a whole session.
 *
 * Exact header TEXT match, case-sensitive — deliberately simpler than
 * `fieldColumns`' alias matching, because the point is not "which field is
 * this", only "is this header text literally the same as one already seen,
 * further left". The first occurrence of a repeated header is kept; every
 * later one is deleted through the same structural machinery a user's own
 * Delete Column goes through, so widths, filters, dropdowns and any formula
 * pointing past it all move with it.
 */
export function dropDuplicateColumns(wb: Workbook, sheet: Sheet): void {
  const seen = new Set<string>();
  const doomed: number[] = [];
  for (let c = 1; c <= sheet.cols; c++) {
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v === undefined || v === null || v === '') continue;
    const text = String(v);
    if (seen.has(text)) doomed.push(c);
    else seen.add(text);
  }
  if (doomed.length === 0) return;
  applyStructural(wb, sheet, 'col', deletionMap(doomed));
}

/**
 * Which fields the given columns hold, read off their headers.
 *
 * Deleting a column on this sheet deletes the FIELD from every usage, so what
 * it names has to come from the header the user is pointing at, not from the
 * position — see fieldColumns.
 */
export function fieldsInColumns(sheet: Sheet, columns: string[], from: number, to: number): string[] {
  const at = fieldColumns(sheet, columns);
  at.delete('name');
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const out: string[] = [];
  for (const [field, c] of at) if (c >= lo && c <= hi) out.push(field);
  return out;
}

/**
 * The field a typed header names: "Time" is `time`, "Sale Date" is `saleDate` —
 * the spelling `prettyField` turns back into the same header, so once the field
 * exists the column is matched to it like any other.
 */
export function fieldFromHeader(header: string): string {
  const words = header.split(/[^A-Za-z0-9]+/).filter((w) => w !== '');
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
}

/**
 * Columns whose header names no field the rows carry — fields being invented.
 *
 * Only for a spec with `newFields`. A header that squashes to a field already
 * there, or to the label, is not new: it is a respelling, and inventing a
 * second field beside the real one is the Blocks bug `headerFields` exists for.
 */
function newFieldColumns(sheet: Sheet, columns: string[], spec: TableSpec, taken: Map<string, number>): Map<string, number> {
  const out = new Map<string, number>();
  if (spec.newFields !== true) return out;
  const known = new Set([...columns, ...spec.labelFields, spec.label].map(squashField));
  const used = new Set(taken.values());
  for (let c = 1; c <= scanWidth(sheet, columns); c++) {
    if (used.has(c)) continue;
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v === undefined || v === null || typeof v === 'boolean') continue;
    const field = fieldFromHeader(String(v));
    if (field === '' || known.has(squashField(field)) || out.has(field)) continue;
    if (spec.hidden?.has(squashField(field))) continue;
    out.set(field, c);
  }
  return out;
}

/** Where each usage name sits, wherever the user has moved it to. */
export function rowsByName(sheet: Sheet, nameCol: number, count: number): Map<string, number> {
  const out = new Map<string, number>();
  for (let r = FIRST_DATA_ROW; r <= FIRST_DATA_ROW + count + 40; r++) {
    const label = getCell(sheet, r, nameCol)?.v;
    if (label === undefined || label === null) continue;
    const name = String(label).trim();
    if (name !== '' && !out.has(name.toLowerCase())) out.set(name.toLowerCase(), r);
  }
  return out;
}

/**
 * Evicts a PINNED field (`spec.order`) that already has a column but is not
 * sitting immediately after the name column, in `order`'s own sequence —
 * self-heals a sheet whose pinned columns drifted out of place because
 * `order` changed in the source code AFTER those columns already existed.
 *
 * `insertNewColumnsSorted` below only ever inserts a field with NO column
 * yet; it never moves one that already has a column, so changing which
 * fields are pinned — or reordering them relative to each other — in
 * `COLUMN_ORDER` has NO EFFECT on an existing sheet's actual layout.
 * Confirmed live: Building ID kept sitting wherever it had been from
 * before `layerGroup` was pinned ahead of it, no matter how many times the
 * source code's `COLUMN_ORDER` changed underneath it — moving the constant
 * in the code is not the same as moving the column on a sheet that already
 * has one.
 *
 * Deletes the stale columns outright, through `deleteBand` (so every
 * cross-sheet formula reference is fixed up the same way a user's own
 * column delete would be), rather than shuffling them into place — the
 * caller's `at` map is simply recomputed afterwards, and
 * `insertNewColumnsSorted` already knows how to place a brand-new field in
 * sorted order; these fields are "new" again the instant their stale
 * column is gone. Returns whether anything was evicted, so the caller
 * knows whether `at` needs rebuilding.
 */
function evictMisorderedPinnedColumns(
  wb: Workbook | undefined,
  sheet: Sheet,
  order: readonly string[],
  at: ReadonlyMap<string, number>,
  nameCol: number
): boolean {
  if (!wb) return false;
  const present = order.filter((f) => at.has(f));
  if (present.length === 0) return false;
  const inPlace = present.every((f, i) => at.get(f) === nameCol + 1 + i);
  if (inPlace) return false;
  deleteBand(wb, sheet, 'col', present.map((f) => at.get(f)!));
  return true;
}

/**
 * Insert every field `columns` has but the sheet does not — each immediately
 * before the nearest already-placed field that sorts after it, so a field
 * the project starts emitting partway through a session lands where it would
 * have if it had been there from the start, rather than stacking up at the
 * far right out of sorted order. Mutates `at` to match.
 *
 * Several new fields anchored on the same existing neighbour insert together,
 * in their own sorted order, as one bulk insert — not one at a time, which
 * would reverse them. Applied anchor-rightmost-first, so a not-yet-applied
 * group's recorded anchor column is never invalidated by a group that
 * inserts to its right.
 *
 * Goes through `insertBand`, the same structural-edit path a user's own
 * column insert takes, because that is what fixes up every OTHER formula in
 * the workbook that might reference this sheet by column letter — a raw
 * shift of just this sheet's own cells would leave those silently wrong.
 */
function insertNewColumnsSorted(
  wb: Workbook,
  sheet: Sheet,
  columns: readonly string[],
  at: Map<string, number>,
  nameCol: number
): void {
  interface Group {
    /** The existing field's current column to insert before — null means "at the end". */
    beforeCol: number | null;
    fields: string[];
  }
  const groups: Group[] = [];
  let pending: string[] = [];
  for (const field of columns) {
    if (at.has(field)) {
      if (pending.length > 0) {
        groups.push({ beforeCol: at.get(field)!, fields: pending });
        pending = [];
      }
    } else {
      pending.push(field);
    }
  }
  if (pending.length > 0) groups.push({ beforeCol: null, fields: pending });

  groups.sort((a, b) => (b.beforeCol ?? Infinity) - (a.beforeCol ?? Infinity));

  for (const group of groups) {
    const insertAt = group.beforeCol ?? Math.max(nameCol, ...at.values(), nameCol) + 1;
    insertBand(wb, sheet, 'col', insertAt, group.fields.length);
    for (const [field, col] of at) if (col >= insertAt) at.set(field, col + group.fields.length);
    group.fields.forEach((field, i) => at.set(field, insertAt + i));
  }
}

/**
 * Write the project's usages onto the sheet.
 *
 * `preserveEdits` keeps anything TYPED that has not been pushed — otherwise a
 * bake arriving mid-edit would silently discard the user's work. It is off for
 * an explicit Reload.
 *
 * "Typed" is decided against the remembered project value, not against the
 * incoming one. Diffing the sheet against fresh data cannot tell the two apart:
 * a cell the user changed and a cell Giraffe changed both read as "the sheet
 * disagrees". Treating the second as an edit meant a usage edited in Giraffe
 * never reached the sheet and sat there as something to push, when there was
 * nothing to push and the sheet was merely out of date.
 */
export function writeTable(
  sheet: Sheet,
  rows: readonly EntityRow[],
  spec: TableSpec,
  preserveEdits = true,
  wb?: Workbook
): void {
  const columns = columnsOf(rows, spec);

  // Who sat where on the LAST write, before anything below can touch it —
  // what makes an orphaned row (one that was an entity's and is no longer
  // any current entity's) findable regardless of where it sits, not just
  // past the last row still in use. See the cleanup pass at the bottom.
  const priorRows = sheet.entityRows ?? {};

  // No Name header anywhere means the layout is past repairing — column 1 was
  // deleted, taking the row labels with it. Start again rather than write rows
  // against labels that are actually somebody's sale prices.
  let nameCol = nameColumn(sheet, columns, spec);
  if (nameCol === null) {
    sheet.cells = {};
    sheet.entityBase = undefined;
    sheet.entityRows = undefined;
    sheet.entityKeys = undefined;
    nameCol = 1;
  }

  const at = fieldColumns(sheet, columns);
  at.delete('name');
  if (evictMisorderedPinnedColumns(wb, sheet, spec.order, at, nameCol)) {
    at.clear();
    for (const [f, c] of fieldColumns(sheet, columns)) at.set(f, c);
    at.delete('name');
  }
  // A field with no column had nowhere to be typed, so nothing about it can be
  // an unpushed edit — it gets a fresh column and the project's value
  // straight into it.
  const onSheet = new Set(at.keys());
  if (wb && columns.some((f) => !at.has(f))) {
    insertNewColumnsSorted(wb, sheet, columns, at, nameCol);
  } else {
    // No workbook to fix formulas up against — fall back to appending every
    // new field at the right end, past anything the user has headed
    // themselves (a field still being invented is not a free column to
    // write over).
    let free = Math.max(nameCol, ...at.values()) + 1;
    const headed = (c: number): boolean => {
      const v = getCell(sheet, HEADER_ROW, c)?.v;
      return v !== undefined && v !== null && String(v).trim() !== '';
    };
    for (const field of columns) {
      if (at.has(field)) continue;
      while (headed(free)) free += 1;
      at.set(field, free);
      free += 1;
    }
  }

  const base = sheet.entityBase;
  const nextBase: Record<string, CellValue> = {};
  /** Which row each thing landed on, so a retyped label reads as a rename. */
  const nextRows: Record<number, string> = {};
  /** Same, by `EntityRow.key` — see its own doc comment and `Sheet.entityKeys`. */
  const nextKeys: Record<number, string> = {};
  const rowOf = rowsByName(sheet, nameCol, rows.length);
  const rowByKey = new Map<string, number>();
  for (const [r, k] of Object.entries(sheet.entityKeys ?? {})) rowByKey.set(k, Number(r));
  /**
   * A row with a `key` is found by it, not by re-reading the name cell —
   * user-requested: two rows can now legitimately show the identical name
   * (the " (1)"/" (2)" disambiguator was removed), so name-text matching
   * alone can no longer tell them apart.
   */
  const resolveRow = (row: EntityRow): number | undefined =>
    row.key !== undefined ? rowByKey.get(row.key) : rowOf.get(row.name.toLowerCase());

  const keep = new Set<string>();
  /**
   * An unpushed edit's own CELL (value, formula, format — whatever it is),
   * by matchKey then field — `spec.forceSort` tables only. A row that keeps
   * its position (the default) just leaves the cell where it already sits
   * (`keep`, above); a row that gets a FRESH position every write cannot —
   * the edit has to be read off the row's OLD position, here, before
   * anything below starts overwriting, and replayed onto its new one in
   * the write loop.
   */
  const carried = new Map<string, Map<string, Cell>>();
  if (preserveEdits && base !== undefined) {
    for (const row of rows) {
      const matchKey = matchKeyOf(row);
      const r = resolveRow(row);
      if (r === undefined) continue;
      for (const field of columns) {
        const key = baseKey(matchKey, field);
        if (!(key in base) || !onSheet.has(field)) continue;
        const cell = getCell(sheet, r, at.get(field)!);
        // A formula in a usage cell is the user's, whatever it evaluates to.
        if (cell?.f === undefined && sameValue(cell?.v, base[key])) continue;
        if (spec.forceSort) {
          const fields = carried.get(matchKey) ?? new Map<string, Cell>();
          fields.set(field, cell ?? {});
          carried.set(matchKey, fields);
        } else {
          keep.add(`${matchKey} ${field}`);
        }
      }
    }
  }

  setCellValue(sheet, HEADER_ROW, nameCol, spec.label);
  bold(sheet, HEADER_ROW, nameCol);
  for (const field of columns) {
    const c = at.get(field)!;
    const raw = typeof spec.rawHeaders === 'function' ? spec.rawHeaders(field) : spec.rawHeaders === true;
    const header = raw ? field : spec.units === null ? prettyField(field) : columnHeader(field, spec.units);
    setCellValue(sheet, HEADER_ROW, c, header);
    bold(sheet, HEADER_ROW, c);
    /*
     * A unit makes a header longer than the 96px default, and a clipped
     * "Sale Price (per…" is exactly the ambiguity the unit was added to
     * remove. Estimated from the character count rather than measured — the
     * model has no canvas — and only where the sheet has no width of its own,
     * so a column the user has sized stays where they put it.
     */
    if (spec.units !== null && sheet.colWidths[c] === undefined && unitSuffix(field, spec.units) !== '') {
      sheet.colWidths[c] = Math.min(170, 8 * header.length + 16);
    }
  }

  applyLists(sheet, at, spec);

  /*
   * A usage keeps the row it is already on, so a sheet the user has sorted or
   * reordered is not shuffled back underneath them. A usage with no row yet
   * takes the first one nobody has claimed — falling back to its index in the
   * data would drop it on top of whichever usage the user moved there.
   *
   * EXCEPT `spec.forceSort`: every row takes a fresh, strictly sequential
   * position in `rows`' own order, full stop — existing position is never
   * consulted for placement (only for `carried`, above, to read an edit off
   * before this overwrites it).
   */
  const claimed = new Set<number>();
  if (!spec.forceSort) {
    for (const row of rows) {
      const r = resolveRow(row);
      if (r !== undefined) claimed.add(r);
    }
  }
  let nextRow = FIRST_DATA_ROW;
  const rowFor = (row: EntityRow): number => {
    if (!spec.forceSort) {
      const existing = resolveRow(row);
      if (existing !== undefined) return existing;
    }
    while (claimed.has(nextRow)) nextRow += 1;
    claimed.add(nextRow);
    return nextRow;
  };

  rows.forEach((row) => {
    const name = row.name;
    const matchKey = matchKeyOf(row);
    const r = rowFor(row);
    nextRows[r] = name;
    if (row.key !== undefined) nextKeys[r] = row.key;
    setCellValue(sheet, r, nameCol, name);
    const carriedFields = carried.get(matchKey);
    for (const field of columns) {
      const shown = displayable(row.fields[field]);
      // Remembered either way: the baseline is what the PROJECT says, and an
      // unpushed edit sitting on top of it does not change that.
      nextBase[baseKey(matchKey, field)] = shown;
      const carriedCell = carriedFields?.get(field);
      if (carriedCell !== undefined) {
        // Replay the edit's own cell onto the row's new position, verbatim.
        sheet.cells[`${r},${at.get(field)!}`] = carriedCell;
        continue;
      }
      if (keep.has(`${matchKey} ${field}`)) continue; // an unpushed edit stands
      const c = at.get(field)!;
      /*
       * The field's format is where a cell STARTS, not a rule it is held to.
       *
       * This sheet is rewritten from the project after every push, and passing
       * the field's format each time stamped it back over whatever the user had
       * chosen — `formatForField` answers "general" for a field it does not
       * recognise, so pasting a currency into one showed the currency for an
       * instant and then snapped to General when the push came back.
       */
      setCellValue(sheet, r, c, shown, getCell(sheet, r, c)?.fmt ?? formatForField(field));
      const prov = row.provenance?.[field];
      if (prov === 'calculated') colourCell(sheet, r, c, 'var(--gt-accent)');
      else if (prov === 'inherited') colourCell(sheet, r, c, 'var(--gt-muted)');
      else clearCellColor(sheet, r, c);
    }
  });

  sheet.entityBase = nextBase;
  sheet.entityRows = nextRows;
  sheet.entityKeys = nextKeys;

  const width = Math.max(nameCol, ...at.values());

  /*
   * A row that WAS an entity's last write (`priorRows`, captured before
   * anything above could touch it) and is no longer ANY current entity's
   * (not in `claimed`) is a deleted thing's leftover — cleared regardless of
   * where it sits, not only past the tail. Confirmed live: deleting a
   * feature that was not alphabetically last — the common case, rows sort
   * by name, not by creation order — left its row sitting there with stale
   * data forever, because the OLD cleanup below only ever trimmed rows
   * after the highest row still in use, and a surviving row never shifts
   * to fill a gap (a usage keeps the row it is already on, by design, so a
   * sheet the user has reordered is not shuffled back underneath them).
   */
  for (const key of Object.keys(priorRows)) {
    const r = Number(key);
    if (claimed.has(r)) continue;
    for (let c = 1; c <= width; c++) delete sheet.cells[`${r},${c}`];
  }

  // Clear whatever a shorter list left behind, past the above: a sheet from
  // before `entityRows` tracked every row individually, or just padding
  // beyond anything either pass above knew to look at.
  const after = (claimed.size > 0 ? Math.max(...claimed) : FIRST_DATA_ROW - 1) + 1;
  for (let r = after; r <= after + 40; r++) {
    for (let c = 1; c <= width; c++) delete sheet.cells[`${r},${c}`];
  }
}

/**
 * Hang the spec's lists on their fields' columns.
 *
 * Keyed by FIELD and re-applied on every write, for two reasons. The column may
 * have moved, and the list belongs to the field rather than to the position.
 * And what is ON the list can change — a new drawing layer is a new option, and
 * a list frozen as the project was when the sheet was made greys out everything
 * added since.
 *
 * A list left behind on a column the field has left is taken down, so it does
 * not become the next occupant's list. Recognised by its CONTENTS, which is
 * what keeps a list the user made themselves from being mistaken for one of
 * these and quietly removed.
 */
function applyLists(sheet: Sheet, at: Map<string, number>, spec: TableSpec): void {
  const lists = Object.entries(spec.lists ?? {});
  if (lists.length === 0) return;

  const wanted = new Map<number, ColumnList>();
  for (const [field, list] of lists) {
    const c = at.get(field);
    if (c !== undefined) wanted.set(c, list);
  }
  const contents = (list: ColumnList): string => list.options.join('\u0000');
  const ours = new Set(lists.map(([, list]) => contents(list)));

  const next = { ...(sheet.dropdowns ?? {}) };
  for (const [key, list] of Object.entries(next)) {
    if (!wanted.has(Number(key)) && ours.has(contents(list))) delete next[Number(key)];
  }
  for (const [c, list] of wanted) next[c] = list;
  sheet.dropdowns = Object.keys(next).length > 0 ? next : undefined;
}

/** A nested value is shown as JSON so it round-trips; scalars pass through. */
function displayable(value: unknown): CellValue {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') return value;
  return JSON.stringify(value);
}


export interface EntityChange {
  name: string;
  field: string;
  from: unknown;
  to: CellValue;
}

/** A name typed on a row the project has no usage for. */
export interface EntityAddition {
  name: string;
  fields: Record<string, CellValue>;
}

/** A row whose label was typed over: the same thing, under a new name. */
export interface EntityRename {
  from: string;
  to: string;
  /** What the project holds for the old name, so nothing is lost in the move. */
  fields: Record<string, unknown>;
}

export interface EntityDiff {
  changes: EntityChange[];
  /** Usages whose row label was cleared — Push offers to delete them. */
  removed: string[];
  /** Rows naming a usage the project does not have — Push creates them. */
  added: EntityAddition[];
  /**
   * Rows renamed in place. The new name is in `added` as well — creating it is
   * how a rename is done — and this is the old name to drop afterwards.
   */
  renamed: EntityRename[];
}

/**
 * What the sheet says that the project does not.
 *
 * Matched by NAME in both directions — rows by their label, columns by their
 * header — never by position. Positionally, deleting one row shifts every row
 * below it and each lines up against the wrong usage (one deletion read as 476
 * changes), and deleting one column shifts every field left and each value is
 * pushed into its neighbour's field. Sorting or reordering did the same.
 * EXCEPT a row whose `EntityRow.key` is set (Features): found by that instead
 * of by name, since two such rows can now legitimately share the identical
 * display name — see `EntityRow.key`'s own doc comment.
 *
 * A field with no column on the sheet is not compared. There is nothing to read
 * for it, and reading the next column along is exactly the bug above.
 */
export function tableDiff(sheet: Sheet, rows: readonly EntityRow[], spec: TableSpec): EntityDiff {
  const known = columnsOf(rows, spec);
  const changes: EntityChange[] = [];
  const removed: string[] = [];
  const added: EntityAddition[] = [];
  const renamed: EntityRename[] = [];

  const nameCol = nameColumn(sheet, known, spec);
  // No Name header: the sheet is not a usages table any more. Say nothing
  // rather than offer to delete every usage in the project.
  if (nameCol === null) return { changes, removed, added, renamed };

  const at = fieldColumns(sheet, known);
  at.delete('name');
  // A column headed with a field nobody has yet: every value typed in it is a
  // change from nothing, and the push creates the field.
  const invented = newFieldColumns(sheet, known, spec, at);
  for (const [field, c] of invented) at.set(field, c);
  const columns = [...known, ...invented.keys()];
  const rowOf = rowsByName(sheet, nameCol, rows.length);
  const rowByKey = new Map<string, number>();
  for (const [r, k] of Object.entries(sheet.entityKeys ?? {})) rowByKey.set(k, Number(r));
  const resolveRow = (row: EntityRow): number | undefined =>
    row.key !== undefined ? rowByKey.get(row.key) : rowOf.get(row.name.toLowerCase());
  const knownNames = new Set(rows.map((row) => row.name.toLowerCase()));

  for (const row of rows) {
    const name = row.name;
    const r = resolveRow(row);

    // Not found by key or name: the row was deleted or cleared.
    if (r === undefined) {
      removed.push(name);
      continue;
    }

    for (const field of columns) {
      // A nested value is shown as JSON and is not something to edit in a cell.
      if (spec.isNested?.(field)) continue;
      const prov = row.provenance?.[field];
      /*
       * No per-cell provenance at all (Usages, Blocks): the old column-level
       * rule, unchanged. `calculated` is pure engine/geometry output with no
       * raw property to promote to — skipped the same way a `readOnly` field
       * always was. `raw` and `inherited` fall through: `current` below is
       * already whichever value is currently SHOWN (a real raw property for
       * `raw`, the baked/inherited value for `inherited`), so an untouched
       * inherited cell compares equal to itself and never diffs — only an
       * edited one does, and that edit is exactly how it gets a raw property
       * of its own.
       */
      if (prov === undefined) {
        if (spec.readOnly?.has(field)) continue;
      } else if (prov === 'calculated') {
        continue;
      }
      const c = at.get(field);
      if (c === undefined) continue; // no column for it: nothing was said
      const cell = getCell(sheet, r, c);
      // A formula someone put in a usage cell is their business, not a value
      // to push back into the project.
      if (cell?.f !== undefined) continue;
      const typed = cell?.v;
      const current = row.fields[field];
      if (sameValue(typed, current)) continue;
      changes.push({ name, field, from: current, to: (typed ?? '') as CellValue });
    }
  }

  // A name the project does not have is a NEW usage. Typing one into a blank
  // row is the obvious way to add one, and the only reason it did not work
  // before is that the diff only ever walked the usages the project already
  // had, so a row it did not recognise was invisible.
  for (const [lower, r] of rowOf) {
    if (knownNames.has(lower)) continue;
    const name = String(getCell(sheet, r, nameCol)!.v).trim();
    const fields: Record<string, CellValue> = {};
    for (const field of columns) {
      if (spec.isNested?.(field) || spec.readOnly?.has(field)) continue;
      const c = at.get(field);
      if (c === undefined) continue;
      const cell = getCell(sheet, r, c);
      if (cell?.f !== undefined) continue;
      const typed = cell?.v;
      if (typed === undefined || typed === '') continue;
      fields[field] = typed;
    }
    added.push({ name, fields });
  }

  /*
   * A label typed over its own row is a RENAME, not a deletion beside an
   * unrelated addition.
   *
   * By name alone the two are identical — the old name is nowhere on the sheet
   * and the new one is nowhere in the project — which is why renaming a usage
   * used to raise a red "Delete 1 usage?" that had to be armed before the
   * ordinary Push would carry it. The ROW is what tells them apart: it still
   * holds whatever the old name held. `entityRows` remembers who sat where when
   * the sheet was last written from the project.
   */
  const wasAt = sheet.entityRows ?? {};
  const byName = new Map(rows.map((row) => [row.name.toLowerCase(), row]));
  for (const addition of added) {
    const r = rowOf.get(addition.name.toLowerCase());
    const before = r === undefined ? undefined : wasAt[r];
    if (before === undefined) continue;
    const i = removed.findIndex((name) => name.toLowerCase() === before.toLowerCase());
    if (i < 0) continue;
    const [from] = removed.splice(i, 1);
    renamed.push({ from: from!, to: addition.name, fields: byName.get(before.toLowerCase())?.fields ?? {} });
  }

  return { changes, removed, added, renamed };
}

function sameValue(typed: unknown, current: unknown): boolean {
  if (typed === undefined || typed === '') {
    return current === undefined || current === null || current === '';
  }
  if (typeof current === 'number' && typeof typed === 'number') {
    // Float noise from a percentage round-trip is not an edit.
    return Math.abs(current - typed) < 1e-9;
  }
  return String(typed) === String(current ?? '');
}

/**
 * Respell a sheet that is already on screen into a different unit system.
 *
 * Needed because these sheets carry a BASELINE (`entityBase`) of what the
 * project said, and unpushed edits are found by disagreeing with it. Switch the
 * units and rewrite from the project alone, and every cell disagrees with a
 * baseline still in the old units — so a sheet nobody had touched would offer
 * to push its entire contents back. Converting the cells and the baseline
 * together keeps the comparison meaningful: an untouched cell still matches, an
 * edit still stands out, and what the user typed is carried into the new units
 * rather than thrown away.
 *
 * Formulas are left alone: a formula is the user's arithmetic, and its inputs
 * are converted underneath it.
 */
export function reunitTable(sheet: Sheet, columns: string[], from: UnitSystem, to: UnitSystem): void {
  if (from === to) return;

  const at = fieldColumns(sheet, columns);
  at.delete('name');
  const dimOfColumn = new Map<number, Dimension>();
  for (const [field, c] of at) {
    const dim = dimensionOf(field);
    if (dim !== 'none') dimOfColumn.set(c, dim);
  }

  for (const [key, cell] of Object.entries(sheet.cells)) {
    if (cell.f !== undefined || typeof cell.v !== 'number' || !Number.isFinite(cell.v)) continue;
    const comma = key.indexOf(',');
    const row = Number(key.slice(0, comma));
    const dim = dimOfColumn.get(Number(key.slice(comma + 1)));
    if (row <= HEADER_ROW || dim === undefined) continue;
    sheet.cells[key] = { ...cell, v: reunit(cell.v, dim, from, to) };
  }

  if (sheet.entityBase !== undefined) {
    const next: Record<string, CellValue> = {};
    for (const [key, value] of Object.entries(sheet.entityBase)) {
      const dim = dimensionOf(fieldOfBaseKey(key));
      next[key] =
        dim !== 'none' && typeof value === 'number' && Number.isFinite(value)
          ? reunit(value, dim, from, to)
          : value;
    }
    sheet.entityBase = next;
  }
}

/**
 * Undo the display conversion, so what reaches Giraffe is metric.
 *
 * The project is metric whatever the sheet is showing, and this is the only
 * gate between the two: a `floorToFloor` typed as 10.5 ft has to arrive as
 * 3.2 m, and a rate typed as $75/ft² as $807/m². Applied at the push, not in
 * `usageDiff`, because the same diff is what the toolbar's tooltip lists — and
 * that should read in the units the person is looking at.
 *
 * `renamed.fields` matters as much as the typed changes: it is the old usage's
 * whole contents, read off the sheet's own (converted) rows and written back
 * under the new name, so leaving it in feet would rewrite the project's rates.
 */
export function toProjectUnits(diff: EntityDiff, system: UnitSystem): EntityDiff {
  if (system === 'metric') return diff;
  const back = (fields: Record<string, unknown>): Record<string, unknown> =>
    reunitFields(fields, system, 'metric');
  return {
    changes: diff.changes.map((c) => ({
      ...c,
      from: back({ [c.field]: c.from })[c.field],
      to: toProjectValue(c.field, c.to, system),
    })),
    removed: diff.removed,
    added: diff.added.map((a) => {
      const fields: Record<string, CellValue> = {};
      for (const [field, value] of Object.entries(a.fields)) {
        fields[field] = toProjectValue(field, value, system);
      }
      return { name: a.name, fields };
    }),
    renamed: diff.renamed.map((r) => ({ ...r, fields: back(r.fields) })),
  };
}
